import {
  AfterViewChecked, ChangeDetectionStrategy, Component, computed,
  inject, OnInit, signal, ViewChild,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { LucideAngularModule, ChevronDown, ChevronUp, MessageSquarePlus, MessagesSquare } from 'lucide-angular';

import { LayoutService }         from '../../../core/services/layout.service';
import { AuthService }           from '../../../core/services/auth.service';
import { ActiveTenantService }   from '../../../core/services/active-tenant.service';
import { PermissionsService }    from '../../../core/services/permissions.service';
import { UserSettingsService }   from '../../../core/services/user-settings.service';
import { PlatformAdminService }     from '../../../core/services/platform-admin.service';
import { AnnouncementsService }      from '../../../core/services/announcements.service';
import { FeedbackModalComponent }    from '../../../features/feedback/feedback-modal/feedback-modal.component';
import { FeedbackService }           from '../../../core/services/feedback.service';
import { PlatformSettingsService }   from '../../../core/services/platform-settings.service';
import { DOCS_URL }              from '../../../core/utils/external-links';

@Component({
  selector: 'app-mobile-drawer',
  standalone: true,
  templateUrl: './mobile-drawer.component.html',
  styleUrls: ['./mobile-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, RouterLinkActive, LucideAngularModule, FeedbackModalComponent],
})
export class MobileDrawerComponent implements OnInit, AfterViewChecked {
  protected readonly layout       = inject(LayoutService);
  protected readonly auth         = inject(AuthService);
  protected readonly activeTenant = inject(ActiveTenantService);
  protected readonly permissions  = inject(PermissionsService);
  protected readonly userSettings = inject(UserSettingsService);
  protected readonly platformAdmin          = inject(PlatformAdminService);
  protected readonly announcementsService     = inject(AnnouncementsService);
  protected readonly platformSettingsService  = inject(PlatformSettingsService);

  protected readonly ChevronDown      = ChevronDown;
  protected readonly ChevronUp        = ChevronUp;
  protected readonly FeedbackIcon     = MessageSquarePlus;
  protected readonly MyFeedbackIcon   = MessagesSquare;
  protected readonly docsUrl          = DOCS_URL;

  private readonly feedbackService = inject(FeedbackService);
  protected readonly feedbackOpen  = signal(false);
  @ViewChild('feedbackModal') private readonly feedbackModalRef?: FeedbackModalComponent;

  protected readonly tenantOpen = signal(false);

  // Capability-gated nav items
  protected readonly canSeeAuditLogs = this.permissions.hasTenantCapability('audit_log_viewer');
  protected readonly canSeeSecurity  = this.permissions.hasTenantCapability('security_viewer');
  protected readonly canSeeBilling   = this.permissions.hasTenantCapability('billing_access');
  protected readonly canSeeMembers   = this.permissions.hasTenantRole('owner');
  protected readonly canSeeSettings  = this.permissions.hasTenantRole('owner');

  protected readonly tenantMembersLink = computed(() => {
    const tid = this.activeTenant.activeId();
    return tid ? ['/tenants', tid, 'members'] : null;
  });

  protected readonly tenantSettingsLink = computed(() => {
    const tid = this.activeTenant.activeId();
    return tid ? ['/tenants', tid, 'settings'] : null;
  });

  protected readonly tenantName = computed(() =>
    this.activeTenant.tenants().find(t => t.id === this.activeTenant.activeId())?.name ?? '-',
  );

  protected readonly displayName = computed(() => {
    const custom = this.userSettings.displayName().trim();
    if (custom) return custom;
    const user = this.auth.currentUser();
    return user?.name || user?.email || '';
  });

  protected readonly initials = computed(() =>
    this.displayName().charAt(0).toUpperCase() || '?',
  );

  protected readonly userEmail = computed(() =>
    this.auth.currentUser()?.email ?? '',
  );

  ngOnInit(): void {
    if (this.auth.isPlatformAdmin()) {
      this.platformSettingsService.loadAdminUnreadCount();
      this.platformSettingsService.loadRestartStatus();
    }
  }

  close():  void { this.layout.closeDrawer(); this.tenantOpen.set(false); }
  onBackdropClick(e: Event): void { if (e.target === e.currentTarget) this.close(); }
  logout(): void { this.auth.logout(); }

  openFeedback(): void {
    this.feedbackService.loadConfig();
    this.feedbackOpen.set(true);
    this.close();
  }

  closeFeedback(): void { this.feedbackOpen.set(false); }

  ngAfterViewChecked(): void {
    if (this.feedbackModalRef && !this.feedbackModalRef.onClose) {
      this.feedbackModalRef.onClose = () => this.closeFeedback();
    }
  }

  async switchTenant(id: string): Promise<void> {
    if (id === this.activeTenant.activeId()) { this.tenantOpen.set(false); return; }
    await this.activeTenant.switch(id);
    this.tenantOpen.set(false);
  }
}
